import { type FormValue, SchemaForm } from "@/components/schema-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ModelSchema } from "@/features/inference/queries";
import type { Family } from "@/lib/family";
import * as Tabs from "@radix-ui/react-tabs";
import { Copy, Eye, EyeOff, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export interface RequestPanelProps {
  modelId: string | null;
  revisionNo: number | null;
  family: Family | null;
  schema: ModelSchema | null;
  secret: string;
  onSecretChange: (s: string) => void;
  onSubmit: (payload: { kind: "single" | "batch"; inputs: unknown }) => Promise<void>;
  busy: boolean;
}

function buildCurl(
  modelId: string | null,
  family: Family | null,
  revisionNo: number | null,
  secret: string,
  payload: unknown,
): string {
  if (!modelId) return "# Select a model first";
  if (!family) return "# Select a model with a supported task_type first";
  const url =
    family === "forecast"
      ? `/inference/v1/models/${modelId}/forecast`
      : `/inference/v1/models/${modelId}/${family}/predict`;
  const body =
    family === "forecast"
      ? JSON.stringify(
          { revision_no: revisionNo, ...(payload as Record<string, unknown>) },
          null,
          2,
        )
      : JSON.stringify({ revision_no: revisionNo, inputs: payload }, null, 2);
  return `curl -X POST '${window.location.origin}${url}' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer ${secret || "<YOUR_API_KEY>"}' \\
  -d '${body}'`;
}

function parseBatch(raw: string): unknown[] | null {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as unknown[];
    return null;
  } catch {
    return null;
  }
}

export function RequestPanel({
  modelId,
  revisionNo,
  family,
  schema,
  secret,
  onSecretChange,
  onSubmit,
  busy,
}: RequestPanelProps) {
  const [tab, setTab] = useState("form");
  const [formValues, setFormValues] = useState<FormValue>({});
  const [jsonText, setJsonText] = useState("{}");
  const [batchText, setBatchText] = useState("[]");
  const [secretVisible, setSecretVisible] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [restoreCounter, setRestoreCounter] = useState(0);

  // Listen for inference:restore custom events dispatched from the ResponsePanel history
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent<Record<string, unknown>>).detail;
      setFormValues(d as FormValue);
      setJsonText(JSON.stringify(d, null, 2));
      setRestoreCounter((c) => c + 1);
    };
    window.addEventListener("inference:restore", handler);
    return () => window.removeEventListener("inference:restore", handler);
  }, []);

  useEffect(() => {
    if (family === "forecast" && (jsonText === "{}" || jsonText === "")) {
      setJsonText(JSON.stringify({ quantile_levels: [0.1, 0.5, 0.9] }, null, 2));
      setTab("json");
    }
  }, [family, jsonText]);

  const handleJsonChange = (val: string) => {
    setJsonText(val);
    try {
      JSON.parse(val);
      setJsonError(null);
    } catch {
      setJsonError("Invalid JSON");
    }
  };

  const handleBatchChange = (val: string) => {
    setBatchText(val);
    const parsed = parseBatch(val);
    if (parsed === null) {
      setBatchError("Must be a JSON array");
    } else {
      setBatchError(null);
    }
  };

  const handleCopyCurl = () => {
    const curl = buildCurl(
      modelId,
      family,
      revisionNo,
      secret,
      family === "forecast"
        ? (() => {
            try {
              return JSON.parse(jsonText);
            } catch {
              return {};
            }
          })()
        : formValues,
    );
    navigator.clipboard.writeText(curl).then(() => toast.success("Copied"));
  };

  const handleSend = () => {
    if (family === "forecast") {
      try {
        const parsed = JSON.parse(jsonText);
        void onSubmit({ kind: "single", inputs: parsed });
      } catch {
        toast.error("Fix JSON before sending");
      }
      return;
    }
    if (tab === "form" || tab === "curl") {
      void onSubmit({ kind: "single", inputs: formValues });
    } else if (tab === "json") {
      try {
        const parsed = JSON.parse(jsonText);
        void onSubmit({ kind: "single", inputs: parsed });
      } catch {
        toast.error("Fix JSON before sending");
      }
    } else if (tab === "batch") {
      const parsed = parseBatch(batchText);
      if (!parsed) {
        toast.error("Batch must be a valid JSON array");
        return;
      }
      if (parsed.length > 100) {
        toast.error("Batch capped at 100 rows (inline predict)");
        return;
      }
      void onSubmit({ kind: "batch", inputs: parsed });
    }
  };

  const curlText = buildCurl(
    modelId,
    family,
    revisionNo,
    secret,
    family === "forecast"
      ? (() => {
          try {
            return JSON.parse(jsonText);
          } catch {
            return {};
          }
        })()
      : formValues,
  );

  const canSend =
    !busy &&
    !!modelId &&
    !!family &&
    !!secret &&
    (family === "forecast" || tab !== "json" || !jsonError) &&
    (family === "forecast" || tab !== "batch" || !batchError);

  return (
    <div className="flex flex-col rounded-lg border border-border bg-surface overflow-hidden">
      {/* Secret input row */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5 bg-surface-2">
        <span className="text-[10px] font-medium uppercase tracking-wider text-fg-dim whitespace-nowrap">
          API Secret
        </span>
        <div className="relative flex-1">
          <Input
            type={secretVisible ? "text" : "password"}
            value={secret}
            onChange={(e) => onSecretChange(e.target.value)}
            placeholder="sk_live_…"
            className="pr-8 font-mono"
          />
          <button
            type="button"
            aria-label={secretVisible ? "Hide secret" : "Reveal secret"}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-dim hover:text-fg"
            onClick={() => setSecretVisible((v) => !v)}
          >
            {secretVisible ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        </div>
      </div>

      {!family && modelId && (
        <div className="border-b border-warning/40 bg-warning/10 px-4 py-2 text-[11px] text-warning">
          This model's task_type is not supported in the console. Use the API or MCP directly.
        </div>
      )}

      {/* Tabs */}
      <Tabs.Root value={tab} onValueChange={setTab} className="flex flex-col flex-1">
        <Tabs.List className="flex border-b border-border px-4 gap-1">
          {(family === "forecast"
            ? (["json", "curl"] as const)
            : (["form", "json", "batch", "curl"] as const)
          ).map((t) => (
            <Tabs.Trigger
              key={t}
              value={t}
              className="border-b-2 border-transparent px-2 pb-2 pt-2.5 text-xs font-medium capitalize text-fg-muted data-[state=active]:border-accent data-[state=active]:text-fg"
            >
              {t === "curl" ? "cURL" : t.charAt(0).toUpperCase() + t.slice(1)}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {/* Form tab */}
        <Tabs.Content value="form" className="flex-1 overflow-y-auto p-4">
          {!schema && (
            <div className="text-xs text-fg-muted">Select a model to render the schema form.</div>
          )}
          {schema && (
            <SchemaForm
              key={restoreCounter}
              schema={schema}
              value={formValues}
              onChange={setFormValues}
            />
          )}
        </Tabs.Content>

        {/* JSON tab */}
        <Tabs.Content value="json" className="flex-1 flex flex-col p-4 gap-2">
          <textarea
            className="flex-1 min-h-[200px] w-full rounded-md border border-border-strong bg-surface-2 px-3 py-2 font-mono text-xs text-fg resize-none focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/20"
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            spellCheck={false}
          />
          {jsonError && <p className="text-[11px] text-danger">{jsonError}</p>}
        </Tabs.Content>

        {/* Batch tab */}
        <Tabs.Content value="batch" className="flex-1 flex flex-col p-4 gap-2">
          <p className="text-[11px] text-fg-muted">
            Paste a JSON array of input objects. Each element will be predicted.
          </p>
          <textarea
            className="flex-1 min-h-[180px] w-full rounded-md border border-border-strong bg-surface-2 px-3 py-2 font-mono text-xs text-fg resize-none focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/20"
            value={batchText}
            onChange={(e) => handleBatchChange(e.target.value)}
            spellCheck={false}
          />
          {batchError && <p className="text-[11px] text-danger">{batchError}</p>}
        </Tabs.Content>

        {/* cURL tab */}
        <Tabs.Content value="curl" className="flex-1 p-4">
          <div className="relative">
            <pre className="overflow-x-auto rounded-md border border-border bg-surface-2 p-3 font-mono text-[11px] text-fg leading-relaxed whitespace-pre-wrap break-all">
              {curlText}
            </pre>
            <button
              type="button"
              aria-label="Copy cURL"
              className="absolute right-2 top-2 rounded border border-border bg-surface p-1 text-fg-dim hover:text-fg"
              onClick={handleCopyCurl}
            >
              <Copy size={12} />
            </button>
          </div>
        </Tabs.Content>
      </Tabs.Root>

      {/* Footer */}
      <div className="flex items-center justify-end border-t border-border px-4 py-3">
        <Button variant="primary" size="md" disabled={!canSend} onClick={handleSend}>
          <Send size={12} />
          {busy ? "Sending…" : "Send request"}
        </Button>
      </div>
    </div>
  );
}
